import {
  CheckOutlined,
  CloseOutlined,
  EditOutlined,
  IssuesCloseOutlined,
  LockOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { Icon } from "@ant-design/compatible";
import {
  Badge,
  Button,
  Col,
  Collapse,
  message,
  Row,
  Spin,
  Typography,
} from "antd";
import {
  CvConfigDto,
  getConfigApplication,
  updateConfigApplication,
  UpdateConfigApplicationData,
} from "api/generated";
import { handleAxiosError } from "common/helpers/generalUtils";
import { useClient } from "hooks/useClient";
import { useEffect, useState } from "react";
import { ConfigField } from "./components/ConfigFields";
import { FielSectionForm, isFielConfig } from "./components/FielSectionForm";

const SENSITIVE_TYPES = new Set(["PASSWORD", "CERTIFICATE", "PRIVATE_KEY"]);

type EditState = Record<string, { value: string; saving: boolean }>;

function isSensitive(cfg: CvConfigDto) {
  return cfg.dataType ? SENSITIVE_TYPES.has(cfg.dataType) : false;
}

function sortByOrden(nodes: CvConfigDto[]): CvConfigDto[] {
  return [...nodes].sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0));
}

function collectLeafIds(node: CvConfigDto): string[] {
  if (node.type === "CONFIG") return node.id ? [node.id] : [];
  return (node.children ?? []).flatMap(collectLeafIds);
}

// ─── ConfigRow ────────────────────────────────────────────────────────────────

function ConfigRow({
  config,
  editState,
  onStartEdit,
  onCancelEdit,
  onChangeValue,
  onSave,
}: {
  config: CvConfigDto;
  editState: EditState;
  onStartEdit: (id: string, value: string) => void;
  onCancelEdit: (id: string) => void;
  onChangeValue: (id: string, value: string) => void;
  onSave: (config: CvConfigDto) => void;
}) {
  const id = config.id ?? "";
  const editing = editState[id];
  const currentValue = editing?.value ?? config.value ?? "";

  return (
    <Row
      gutter={[16, 8]}
      style={{
        padding: "12px 16px",
        borderBottom: "1px solid #f0f0f0",
        background: editing ? "#f6ffed" : "transparent",
        transition: "background 0.2s",
      }}
    >
      <Col xs={24} sm={9}>
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <Typography.Text strong>
              {config.icon ? <Icon type={config.icon} /> : undefined}{" "}
              {config.description}
            </Typography.Text>
            {config.isProtected && (
              <Typography.Text type="secondary">
                <LockOutlined style={{ color: "#faad14" }} /> Protegido
              </Typography.Text>
            )}
          </div>
          {config.requiresRestart && (
            <Typography.Text type="secondary">
              <ReloadOutlined style={{ color: "#f5222d" }} /> Requiere reinicio
            </Typography.Text>
          )}
          {config.isRequired && config.value === null && (
            <Typography.Text type="secondary">
              <IssuesCloseOutlined style={{ color: "#f5222d" }} /> Requerido
            </Typography.Text>
          )}
        </div>
      </Col>

      <Col xs={24} sm={12}>
        <ConfigField
          config={config}
          value={currentValue}
          readOnly={!editing}
          onChange={(value) => onChangeValue(id, value)}
        />
      </Col>

      <Col
        xs={24}
        sm={3}
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "flex-end",
          gap: 6,
        }}
      >
        {!config.isHidden && !editing && (
          <Button
            type="text"
            size="small"
            icon={<EditOutlined />}
            onClick={() =>
              onStartEdit(id, isSensitive(config) ? "" : (config.value ?? ""))
            }
          />
        )}
        {editing && (
          <>
            <Button
              type="text"
              size="small"
              icon={<CheckOutlined />}
              loading={editing.saving}
              onClick={() => onSave(config)}
            />
            <Button
              size="small"
              type="text"
              icon={<CloseOutlined />}
              onClick={() => onCancelEdit(id)}
              disabled={editing.saving}
            />
          </>
        )}
      </Col>
    </Row>
  );
}

function ConfigSection({
  node,
  editState,
  onStartEdit,
  onCancelEdit,
  onChangeValue,
  onSave,
  onFielSuccess,
  depth = 0,
}: {
  node: CvConfigDto;
  editState: EditState;
  onStartEdit: (id: string, value: string) => void;
  onCancelEdit: (id: string) => void;
  onChangeValue: (id: string, value: string) => void;
  onSave: (config: CvConfigDto) => void;
  onFielSuccess: () => void;
  depth?: number;
}) {
  const children = sortByOrden(node.children ?? []);

  // Separar leaves FIEL de las normales
  const fielLeaves = children.filter(
    (c) => c.type === "CONFIG" && !c.isHidden && isFielConfig(c),
  );
  const normalLeaves = children.filter(
    (c) => c.type === "CONFIG" && !c.isHidden && !isFielConfig(c),
  );
  const subNodes = children.filter((c) => c.type !== "CONFIG");

  const dirtyCount = collectLeafIds(node).filter((id) => editState[id]).length;

  const header = (
    <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <Typography.Text strong>
        {node.icon ? (
          <Icon type={node.icon} className="customized" />
        ) : undefined}{" "}
        {node.name}
      </Typography.Text>
      {node.description && (
        <Typography.Text type="secondary">— {node.description}</Typography.Text>
      )}
      {dirtyCount > 0 && (
        <Badge count={dirtyCount} size="small" color="#faad14" />
      )}
    </span>
  );

  return (
    <Collapse
      defaultActiveKey={[node.id ?? ""]}
      ghost={depth > 0}
      style={{ marginBottom: depth === 0 ? 12 : 4 }}
      accordion
    >
      <Collapse.Panel
        key={node.id ?? ""}
        header={header}
        style={{
          background: depth === 0 ? "#fff" : "transparent",
          border: depth === 0 ? "1px solid #e8e8e8" : "none",
          borderRadius: depth === 0 ? 8 : 0,
          overflow: "hidden",
        }}
      >
        {/* Configs normales */}
        {normalLeaves.length > 0 && (
          <div
            style={{
              border: "1px solid #f0f0f0",
              borderRadius: 6,
              overflow: "hidden",
              marginTop: subNodes.length > 0 ? 12 : 0,
            }}
          >
            {normalLeaves.map((cfg) => (
              <ConfigRow
                key={cfg.id}
                config={cfg}
                editState={editState}
                onStartEdit={onStartEdit}
                onCancelEdit={onCancelEdit}
                onChangeValue={onChangeValue}
                onSave={onSave}
              />
            ))}
          </div>
        )}

        {/* Configs FIEL — agrupados en su propio formulario */}
        {fielLeaves.length > 0 && (
          <div
            style={{
              border: "1px solid #f0f0f0",
              borderRadius: 6,
              overflow: "hidden",
              marginTop:
                normalLeaves.length > 0 || subNodes.length > 0 ? 12 : 0,
            }}
          >
            <FielSectionForm configs={fielLeaves} onSuccess={onFielSuccess} />
          </div>
        )}

        {subNodes.length > 0 && (
          <div style={{ paddingLeft: depth > 0 ? 8 : 0 }}>
            {subNodes.map((sub) => (
              <ConfigSection
                key={sub.id}
                node={sub}
                editState={editState}
                onStartEdit={onStartEdit}
                onCancelEdit={onCancelEdit}
                onChangeValue={onChangeValue}
                onSave={onSave}
                onFielSuccess={onFielSuccess}
                depth={depth + 1}
              />
            ))}
          </div>
        )}
      </Collapse.Panel>
    </Collapse>
  );
}

interface AppConfigurationScreenProps {
  commitChange?: () => void;
}

function AppConfigurationScreen({ commitChange }: AppConfigurationScreenProps) {
  const { client } = useClient();
  const [isLoading, setIsLoading] = useState(false);
  const [tree, setTree] = useState<CvConfigDto[]>([]);
  const [editState, setEditState] = useState<EditState>({});
  const [missingConfigs, setMissingConfigs] = useState(false);

  function loadConfigs() {
    setIsLoading(true);
    getConfigApplication({ client })
      .then(({ data }) => {
        setTree(data ?? []);

        // Después de cargar los datos, iniciar edición para campos requeridos sin valor
        const initialEditState: EditState = {};
        const initializeRequiredFields = (nodes: CvConfigDto[]) => {
          nodes.forEach((node) => {
            if (
              node.type === "CONFIG" &&
              node.isRequired &&
              !node.value &&
              node.id
            ) {
              // Iniciar edición para este campo
              initialEditState[node.id] = {
                value: isSensitive(node) ? "" : "",
                saving: false,
              };
              setMissingConfigs(true);
            }
            if (node.children) {
              initializeRequiredFields(node.children);
            }
          });
        };

        initializeRequiredFields(data ?? []);
        setEditState(initialEditState);
      })
      .catch((err) =>
        handleAxiosError(err, "No fue posible obtener las configuraciones"),
      )
      .finally(() => setIsLoading(false));
  }

  useEffect(() => {
    loadConfigs();
  }, [client]);

  function handleStartEdit(id: string, value: string) {
    setEditState((prev) => ({ ...prev, [id]: { value, saving: false } }));
  }

  function handleCancelEdit(id: string) {
    setEditState((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }

  function handleChangeValue(id: string, value: string) {
    setEditState((prev) => ({ ...prev, [id]: { ...prev[id], value } }));
  }

  function handleSave(config: CvConfigDto) {
    if (!config.id) {
      message.error(`No es posible actualizar esto`);
      return;
    }
    const id = config.id;
    const configKey =
      config.configKey as UpdateConfigApplicationData["path"]["keyWord"];
    const newValue = editState[id]?.value ?? "";

    if (config.value === newValue) {
      handleCancelEdit(id);
      return;
    }

    setEditState((prev) => ({ ...prev, [id]: { ...prev[id], saving: true } }));

    updateConfigApplication({
      client,
      path: { keyWord: configKey },
      query: { newValue },
    })
      .then(() => {
        message.success(`"${config.description ?? config.name}" actualizado`);
        handleCancelEdit(id);
        loadConfigs();
        commitChange?.();
      })
      .catch((err) => {
        handleAxiosError(
          err,
          `No fue posible actualizar "${config.description ?? config.name}"`,
        );
        setEditState((prev) => ({
          ...prev,
          [id]: { ...prev[id], saving: false },
        }));
      });
  }

  function handleFielSuccess() {
    message.success("Firma Electrónica (FIEL) actualizada");
    loadConfigs();
    commitChange?.();
  }

  return (
    <div
      style={{ padding: "24px 24px 48px", maxWidth: 1100, margin: "0 auto" }}
    >
      <div style={{ marginBottom: 24 }}>
        <Typography.Title level={3} style={{ margin: 0 }}>
          Configuración de la aplicación
        </Typography.Title>
        {missingConfigs && (
          <Typography.Text>
            Debes establecer al menos las configuraciones obligatorias *
          </Typography.Text>
        )}
      </div>

      <Spin spinning={isLoading}>
        {sortByOrden(tree).map((node) =>
          node.type === "CONFIG" ? (
            <div
              key={node.id}
              style={{
                background: "#fff",
                border: "1px solid #e8e8e8",
                borderRadius: 8,
                marginBottom: 12,
                overflow: "hidden",
              }}
            >
              <ConfigRow
                config={node}
                editState={editState}
                onStartEdit={handleStartEdit}
                onCancelEdit={handleCancelEdit}
                onChangeValue={handleChangeValue}
                onSave={handleSave}
              />
            </div>
          ) : (
            <ConfigSection
              key={node.id}
              node={node}
              editState={editState}
              onStartEdit={handleStartEdit}
              onCancelEdit={handleCancelEdit}
              onChangeValue={handleChangeValue}
              onSave={handleSave}
              onFielSuccess={handleFielSuccess}
            />
          ),
        )}
      </Spin>
    </div>
  );
}

export default AppConfigurationScreen;
